import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PCLayout from './PCLayout';
import { Experience, Skills, Research, Activities } from './Professional';

const PCProfessionalView = ({ isDarkMode, toggleTheme }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine initial active section based on location state or hash
    const getInitialSection = () => {
        if (location.state && location.state.targetSection) {
            return location.state.targetSection.toLowerCase();
        }
        if (location.hash) {
            const hashSection = location.hash.substring(1);
            if (['experience', 'research', 'skills', 'activities'].includes(hashSection)) {
                return hashSection;
            }
        }
        return 'experience';
    };

    // State to track active section
    const [activeSection, setActiveSection] = useState(getInitialSection());

    // Flag to prevent unnecessary updates when programmatically scrolling
    const isScrollingProgrammatically = useRef(false);

    // Cooldown timer to prevent active indicator flickering
    const cooldownActiveRef = useRef(false);
    const cooldownTimerRef = useRef(null);

    // Container ref for scroll observation
    const contentMainRef = useRef(null);

    // Refs for each section
    const experienceRef = useRef(null);
    const researchRef = useRef(null);
    const skillsRef = useRef(null);
    const activitiesRef = useRef(null);

    // Set cooldown for the active indicator
    const setCooldown = () => {
        // Set cooldown active
        cooldownActiveRef.current = true;

        // Clear any existing cooldown timer
        if (cooldownTimerRef.current) {
            clearTimeout(cooldownTimerRef.current);
        }

        // Set new cooldown timer
        cooldownTimerRef.current = setTimeout(() => {
            cooldownActiveRef.current = false;
        }, 500); // 0.5 second cooldown
    };

    // Handle menu item click
    const handleMenuItemClick = (sectionId) => {
        const aboutMeSections = ['me', 'education', 'hobbies'];
        if (aboutMeSections.includes(sectionId.toLowerCase())) {
            // Navigate to main page with state containing target section
            navigate('/', { state: { targetSection: sectionId.toLowerCase() } });
            return;
        }

        // Miscellaneous sections navigate to /misc route
        const miscSections = ['accolades', 'projects', 'blog'];
        if (miscSections.includes(sectionId.toLowerCase())) {
            navigate('/misc', { state: { targetSection: sectionId.toLowerCase() } });
            return;
        }

        const sectionRefs = {
            'experience': experienceRef,
            'research': researchRef,
            'skills': skillsRef,
            'activities': activitiesRef
        };

        const ref = sectionRefs[sectionId.toLowerCase()];
        if (ref && ref.current) {
            // Set flag to prevent change from triggering another scroll
            isScrollingProgrammatically.current = true;

            // Set the active section immediately
            setActiveSection(sectionId.toLowerCase());

            // Activate cooldown to prevent flickering
            setCooldown();

            // Scroll to section
            ref.current.scrollIntoView({ behavior: 'smooth' });

            // Reset flag after animation completes
            setTimeout(() => {
                isScrollingProgrammatically.current = false;
            }, 1000);
        }
    };

    // Initialize the Professional page by selecting a section
    useEffect(() => {
        // Check if we arrived from AboutMe page with a target section
        let initialSection = 'experience'; // Default

        if (location.state && location.state.targetSection) {
            // Get target section from location state
            const requestedSection = location.state.targetSection;
            if (['experience', 'research', 'skills', 'activities'].includes(requestedSection)) {
                initialSection = requestedSection;
            }
        }
        // Support for hash in URL as fallback for direct links
        else if (location.hash) {
            const hashSection = location.hash.substring(1);
            if (['experience', 'research', 'skills', 'activities'].includes(hashSection)) {
                initialSection = hashSection;
            }
        }

        // Set initial active section (redundant but safe)
        setActiveSection(initialSection);

        // Scroll to the section if we have a ref for it
        const sectionRefs = {
            'experience': experienceRef,
            'research': researchRef,
            'skills': skillsRef,
            'activities': activitiesRef
        };

        const ref = sectionRefs[initialSection];
        if (ref && ref.current) {
            // Set cooldown immediately to prevent observer from overriding active section
            setCooldown();

            // Wait for DOM to fully render
            setTimeout(() => {
                ref.current.scrollIntoView({ behavior: 'smooth' });
                setCooldown(); // Prevent flickering
            }, 100);
        }
    }, [location]);

    // Setup intersection observer to detect which section is visible
    useEffect(() => {
        if (!contentMainRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                // Skip intersection updates if we're in cooldown period
                if (cooldownActiveRef.current) return;

                entries.forEach(entry => {
                    if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                        const sectionId = entry.target.id;

                        // Update active section state only if not in cooldown period
                        setActiveSection(sectionId);
                    }
                });
            },
            {
                root: contentMainRef.current,
                rootMargin: '0px',
                threshold: 0.6, // Section is considered "active" when 60% visible
            }
        );

        // Observe each section
        const sections = [
            experienceRef.current,
            researchRef.current,
            skillsRef.current,
            activitiesRef.current
        ].filter(Boolean);

        sections.forEach(section => {
            if (section) observer.observe(section);
        });

        return () => {
            sections.forEach(section => {
                if (section) observer.unobserve(section);
            });

            // Clear any lingering cooldown timer
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current);
            }
        };
    }, []);

    return (
        <PCLayout
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            activePage={activeSection}
            contentRef={contentMainRef}
            onMenuItemClick={handleMenuItemClick}
        >
            {/* Professional Section components */}
            <div id="experience" ref={experienceRef}>
                <Experience isDarkMode={isDarkMode} />
            </div>

            <div id="research" ref={researchRef}>
                <Research isDarkMode={isDarkMode} />
            </div>

            <div id="skills" ref={skillsRef}>
                <Skills isDarkMode={isDarkMode} />
            </div>

            <div id="activities" ref={activitiesRef}>
                <Activities isDarkMode={isDarkMode} />
            </div>
        </PCLayout>
    );
};

export default PCProfessionalView;